import {
  useEffect,
  useState
} from 'react'
import {
  AtomicBlockUtils,
  convertFromRaw,
  convertToRaw,
  EditorState
} from 'draft-js'
import { Editor } from 'react-draft-wysiwyg'
import {
  Box,
  Button,
  Image,
  Skeleton,
  Stack,
  Text
} from '@chakra-ui/react'
import { setOpenBlogLink } from 'store'
import { motion } from 'framer-motion'
import {
  isMobile,
  toOdyseeEmbed,
  toYouTubeEmbed
} from 'utils/helpers'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import {
  CloseIcon,
  CloseIconTransparent,
  OdyseeLogoIcon,
  PlayIcon
} from 'assets/icons'
import {
  REJECTED,
  SUCCEEDED
} from 'utils/constants'
import { LoadingSpinier } from 'common/Loading'
import { COLORS } from 'utils/styles'

const MotionBox = motion(Box)

const ImageComponent = ({
  block,
  contentState,
  blockProps
}) => {
  const entity = contentState.getEntity(block.getEntityAt(0))
  const { src } = entity.getData()
  const [showX, setShowX] = useState(false)

  return (
    <Box textAlign="center">
      <Stack
        textAlign="center"
        position="relative"
        display="inline-block"
        onMouseEnter={() => setShowX(true)}
        onMouseLeave={() => setShowX(false)}
      >
        <Image
          src={src}
          maxWidth="100%"
          cursor="default"
        />
        <Stack
          aria-label="Remove image"
          backgroundColor="rgba(255, 255, 255, 0.8)"
          padding="2px"
          position="absolute"
          top="2"
          right="2"
          cursor="pointer"
          borderRadius="5px"
          display={showX ? 'flex' : 'none'}
          onClick={(e) => {
            e.stopPropagation()
            document.activeElement?.blur()
            blockProps.removeImage(block.getKey())
          }}
        >
          <CloseIcon />
        </Stack>
      </Stack>
    </Box>
  )
}

const EmbedComponent = ({
  block,
  contentState,
  blockProps
}) => {
  const [isLoaded, setIsLoaded] = useState(false)
  const entity = contentState.getEntity(block.getEntityAt(0))
  const { src } = entity.getData()
  const {
    dispatch,
    removeImage
  } = blockProps
  const [thumbnail, setThumbnail] = useState(null)

  useEffect(() => {
    if (src?.includes('odysee.com')) {
      fetch(process.env.REACT_APP_API_URL + '/posts/get-odysee-thumbnail', {
        method: 'POST',
        body: JSON.stringify({ link: src.replace('$/embed/', '') }),
        headers: { 'Content-type': 'application/json; charset=UTF-8' }
      })
        .then((res) => res.json())
        .then((data) => setThumbnail(data?.imgLink))
        .catch((err) => {
          console.error(err)
          setIsLoaded(true)
        })
    }
  }, [])

  return (
    <Box
      textAlign="center"
      margin="8px 0"
      position="relative"
      display="inline-block"
      width="100%"
    >
      <Stack
        position="relative"
        display="inline-block"
        width="100%"
        maxWidth="560px"
      >
        <Skeleton
          isLoaded={isLoaded}
          fadeDuration={1}
        >
          <Box
            position="relative"
            paddingTop={src?.includes('://odysee.com/') ? '' : '56.25%'}
          >
            {
              src?.includes('://odysee.com/')
                ? (
                  <>
                    <Box
                      position="absolute"
                      top="0"
                      width="100%"
                      height="50px"
                      background="linear-gradient(180deg, black 10%, transparent 100%)"
                    />

                    <Box
                      alt="play"
                      position="absolute"
                      top="50%"
                      left="50%"
                      transform="translate(-50%, -50%)"
                      background="rgba(222,0,80,0.96)"
                      padding="25px"
                      borderRadius="full"
                    >
                      <PlayIcon
                        width="35"
                        height="35"
                      />
                    </Box>

                    <Box
                      alt="odysee-logo"
                      position="absolute"
                      top="-22px"
                      right="12px"
                    >
                      <OdyseeLogoIcon
                        width="34"
                        height="34"
                      />
                    </Box>

                    <Image
                      src={thumbnail}
                      alt="content"
                      onLoad={() => setIsLoaded(true)}
                      width={isLoaded ? 'auto' : '70svw'}
                      height={isLoaded ? 'auto' : '200px'}
                    />
                  </>
                )
                : (
                  <iframe
                    src={src}
                    title="embed"
                    loading="lazy"
                    onLoad={() => setIsLoaded(true)}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      border: 0,
                      borderRadius: '5px'
                    }}
                  />
                )
            }

            <Box
              position="absolute"
              bottom="0"
              right="0"
              zIndex="40"
              background="transparent"
              width="100%"
              height="100%"
              padding="0"
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation()
                document.activeElement?.blur()
                dispatch(setOpenBlogLink(src + '?autoplay=1'))
              }}
              _hover={false}
              _active={false}
              _focusVisible={false}
              _focus={false}
            />
          </Box>

          <Button
            backgroundColor="transparent"
            zIndex="41"
            borderRadius="full"
            position="absolute"
            right="6px"
            top="6px"
            width="20px"
            height="20px"
            padding="0"
            minWidth="0"
            boxShadow="0 4px 10px rgba(0, 0, 0, 0.2)"
            onClick={(e) => {
              e.stopPropagation()
              document.activeElement?.blur()
              removeImage(block.getKey())
            }}
          >
            <Box
              as="span"
              position="relative"
              width="20px"
              height="20px"
              filter="drop-shadow(0 2px 4px rgba(0,0,0,0.6))"
            >
              <CloseIconTransparent />
            </Box>
          </Button>
        </Skeleton>
      </Stack>
    </Box>
  )
}

export const TextEditor = ({
  allowedFiles,
  editMode,
  editPostData,
  uploadedImg,
  countImages,
  setBody,
  setUploadedImg,
  setActiveScroller,
  getNotificationMessage,
  dispatch,
  pillHoverShow,
  pillHoverText,
  showAbsolute,
  DeleteBtn,
  setOpenDeletePost,
  setPillHoverShow,
  openDeletePost,
  onGoingApiCall,
  setPillHoverText,
  DeletePopupText,
  DeletePopupBtn,
  deletePost,
  affinitiesArray,
  setAffinitiesArray,
  deletePostsStatus
}) => {
  const [editorState, setEditorState] = useState(EditorState.createEmpty())
  const MAX_CHARACTERS = 50000

  const insertEmbed = (embedUrl) => {
    const contentState = editorState.getCurrentContent()
    const contentStateWithEntity = contentState.createEntity('EMBED', 'IMMUTABLE', { src: embedUrl })
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey()
    const newEditorState = AtomicBlockUtils.insertAtomicBlock(editorState, entityKey, ' ')
    setEditorState(EditorState.forceSelection(newEditorState, newEditorState.getSelection()))
  }

  const getEmbedUrl = (text) => {
    const trimmed = text.trim()
    if (trimmed.includes('youtube.com') || trimmed.includes('youtu.be')) {
      return toYouTubeEmbed(trimmed)
    }
    if (trimmed.includes('odysee.com')) {
      return toOdyseeEmbed(trimmed)
    }
    return null
  }

  const insertImage = (url) => {
    const contentState = editorState.getCurrentContent()
    const contentStateWithEntity = contentState.createEntity('IMAGE', 'IMMUTABLE', { src: url })
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey()

    const newEditorState = AtomicBlockUtils.insertAtomicBlock(
      editorState,
      entityKey,
      ' '
    )

    setEditorState(EditorState.forceSelection(newEditorState, newEditorState.getSelection()))
  }

  const handleImageUpload = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const url = reader.result
      insertImage(url)
      resolve({ data: { url } })
    }

    reader.onerror = (reason) => reject(reason)

    reader.readAsDataURL(file)
  })

  const removeImage = (blockKey) => {
    const contentState = editorState.getCurrentContent()
    const blockMap = contentState.getBlockMap().delete(blockKey)
    const newContentState = contentState.merge({
      blockMap,
      selectionAfter: contentState.getSelectionAfter()
    })

    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'remove-range'
    )

    setEditorState(newEditorState)
  }

  const handleBeforeInput = (chars) => {
    const currentContent = editorState.getCurrentContent()
    const plainText = currentContent.getPlainText('\n')
    const newText = plainText + chars

    if (newText.length > MAX_CHARACTERS) {
      dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: MAX_CHARACTERS.toLocaleString() + ' characters only',
          isEditProfile: true
        })
      )
      return 'handled'
    }

    return 'not-handled'
  }

  const handleReturn = () => {
    const currentContent = editorState.getCurrentContent()
    const plainText = currentContent.getPlainText('\n')

    if (plainText.length >= MAX_CHARACTERS) {
      dispatch(
        getNotificationMessage({
          type: REJECTED,
          message: MAX_CHARACTERS.toLocaleString() + ' characters only',
          isEditProfile: true
        })
      )
      return 'handled'
    }

    return 'not-handled'
  }

  const handlePastedText = (text) => {
    const embedUrl = getEmbedUrl(text)
    if (embedUrl) {
      insertEmbed(embedUrl)
      return 'handled'
    }

    return handleBeforeInput(text)
  }

  useEffect(() => {
    if (uploadedImg !== '') {
      handleImageUpload(uploadedImg)
    }

    setUploadedImg('')
  }, [uploadedImg])

  useEffect(() => {
    setBody(convertToRaw(editorState.getCurrentContent()))
  }, [editorState])

  useEffect(() => {
    try {
      if (editMode && editPostData.id) {
        const contentState = convertFromRaw(editPostData.body)
        setEditorState(EditorState.createWithContent(contentState))
      }
    } catch (err) {
      console.error(err)
    }
  }, [editPostData])

  return (
    <MotionBox
      initial={{
        y: 0,
        height: 0
      }}
      animate={{
        y: 0,
        height: '100%',
        position: 'relative'
      }}
      transition={{
        type: 'tween',
        duration: isMobile() ? 1 : 0.8
      }}
      border="solid 2px transparent"
      borderRadius="5px"
      _focusWithin={{
        borderColor: 'rgb(49, 130, 206)'
      }}
      height="5em"
      display="flex"
      flexDirection="column"
      overflow="auto"
    >
      <Editor
        editorState={editorState}
        onEditorStateChange={(e) => {
          setEditorState(e)
          countImages(Object.values(convertToRaw(e.getCurrentContent()).entityMap))
        }}
        handleBeforeInput={(chars) => handleBeforeInput(chars)}
        handlePastedText={(chars) => handlePastedText(chars)}
        handleReturn={handleReturn}
        stripPastedStyles
        blockRendererFn={(block) => {
          if (block.getType() === 'atomic') {
            const contentState = editorState.getCurrentContent()
            const entityKey = block.getEntityAt(0)
            if (!entityKey) return null
            const entity = contentState.getEntity(entityKey)

            if (entity.getType() === 'EMBED') {
              return {
                component: EmbedComponent,
                editable: false,
                props: {
                  dispatch,
                  removeImage
                }
              }
            }

            return {
              component: ImageComponent,
              editable: false,
              props: { removeImage }
            }
          }
          return null
        }}
        toolbarHidden
        toolbar={{
          image: {
            urlEnabled: false,
            inputAccept: { allowedFiles },
            uploadCallback: handleImageUpload,
            alt: { present: false, mandatory: false }
          }
        }}
        editorStyle={{
          background: 'rgb(246, 246, 246)',
          width: '100%',
          minHeight: '100%',
          overflowY: 'scroll',
          resize: 'none',
          padding: '0 3% 3% 3%',
          borderRadius: '5px',
          outline: 'none',
          transition: 'border-color 0.2s',
          border: 'solid 1px black',
          cursor: 'text'
        }}
        onFocus={() => setActiveScroller(null)}
        placeholder="Article here"
        name="blog-body"
        spellCheck
      />
      <Text
        opacity={pillHoverShow ? 1 : 0}
        transition="0.5s"
        position="absolute"
        backgroundColor="whitesmoke"
        padding="10px 0px"
        fontSize="20px"
        top={pillHoverShow ? '50%' : '0px'}
        transform={pillHoverShow ? 'translateY(-50%)' : 'none'}
        right="60px"
        left="60px"
        border="1px solid black"
        borderRadius="10px"
        boxShadow="lg"
        textAlign="center"
        zIndex={pillHoverShow ? 100 : -100}
      >
        {pillHoverText}
      </Text>

      {
        Object.keys(editPostData).length
          ? (
            <Stack
              display={showAbsolute ? 'flex' : 'none'}
              position="absolute"
              right="5px"
              bottom="5px"
              zIndex="5"
            >
              <DeleteBtn
                id="delete-post-btn"
                onClick={() => {
                  setOpenDeletePost(true)
                  setPillHoverShow(false)
                }}
                backgroundColor={(openDeletePost || onGoingApiCall() ? COLORS.gray_op8 : COLORS.gold) + ' !important'}
                pointerEvents={openDeletePost || onGoingApiCall() ? 'none' : 'auto'}
                cursor={openDeletePost || onGoingApiCall() ? 'default' : 'pointer'}
                onMouseEnter={() => {
                  if (!isMobile()) {
                    setPillHoverText('Delete article')
                    setPillHoverShow(true)
                  }
                }}
                onMouseLeave={() => {
                  if (!isMobile()) {
                    setPillHoverShow(false)
                  }
                }}
                onTouchStart={() => {
                  setPillHoverText('Delete article')
                  setPillHoverShow(true)
                }}
                onTouchEnd={() => setPillHoverShow(false)}
              >
                Delete
              </DeleteBtn>
            </Stack>
          ) : ''
      }
      <Stack
        opacity={openDeletePost ? 1 : 0}
        transition="1s"
        position="absolute"
        backgroundColor="rgb(228, 151, 144)"
        padding="15px 5px"
        transform={openDeletePost ? 'translateY(-50%)' : 'none'}
        top={openDeletePost ? '50%' : '0px'}
        right="50px"
        left="50px"
        borderRadius="5px"
        boxShadow="lg"
        zIndex={openDeletePost ? 101 : -100}
        flexDir="row"
        justifyContent="center"
        alignItems="center"
        sx={{
          '@media (max-height: 550px)': {
            top: '65% !important'
          }
        }}
      >
        <DeletePopupText>Delete this article?</DeletePopupText>

        <DeletePopupBtn
          id="confirm-delete-post-btn"
          onClick={() => deletePost()}
          backgroundColor={(onGoingApiCall(3) || deletePostsStatus === SUCCEEDED ? COLORS.gray_op8 : COLORS.gold) + ' !important'}
          pointerEvents={onGoingApiCall(3) || deletePostsStatus === SUCCEEDED ? 'none' : 'auto'}
          cursor={onGoingApiCall(3) || deletePostsStatus === SUCCEEDED ? 'default' : 'pointer'}
        >
          {
            onGoingApiCall(3)
              ? (
                <LoadingSpinier
                  width="20px"
                  height="20px"
                  marginTop="5px"
                />
              ) : 'Delete'
          }
        </DeletePopupBtn>

        <DeletePopupBtn
          id="cancel-delete-post-btn"
          onClick={() => setOpenDeletePost(false)}
          backgroundColor={(onGoingApiCall(3) || deletePostsStatus === SUCCEEDED ? COLORS.gray_op8 : COLORS.gold) + ' !important'}
          pointerEvents={onGoingApiCall(3) || deletePostsStatus === SUCCEEDED ? 'none' : 'auto'}
          cursor={onGoingApiCall(3) || deletePostsStatus === SUCCEEDED ? 'default' : 'pointer'}
        >
          Cancel
        </DeletePopupBtn>
      </Stack>

      <Stack
        rowGap="10px"
        spacing="unset"
        wrap="wrap"
        direction="row"
        display={affinitiesArray.length > 0 && showAbsolute ? 'flex' : 'none'}
        position="absolute"
        left="5px"
        bottom="5px"
        zIndex="5"
      >
        {affinitiesArray.map((item) => (
          <Stack
            className="blog-chosen-affinity"
            direction="row"
            width="max-content"
            alignItems="center"
            color="#F8F8FF"
            border="solid 1px black"
            borderRadius="5px"
            bg={onGoingApiCall() ? COLORS.gray_op8 : COLORS.gold}
            p="0 2px"
            data-name="blog-affinity"
            fontSize="11px"
            cursor={onGoingApiCall() ? 'default' : 'pointer'}
            pointerEvents={onGoingApiCall() ? 'none' : 'auto'}
            onClick={() => setAffinitiesArray([])}
          >
            <Text data-name="affinity-title">{item.title}</Text>
            <Stack marginLeft="-8px" marginRight="2px" width="11px" height="11px">
              <CloseIcon fill="#F8F8FF" />
            </Stack>
          </Stack>
        ))}
      </Stack>
    </MotionBox>
  )
}
